import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { useTranslation } from 'react-i18next';
import { useRemoveCreditCard } from '@/hooks/payments/creditCard/useRemoveCreditCard';
import type { CreditCardAccount } from '@/lib/payments/creditCardAccount.types';
import { ConfirmRemoveDialog } from './ConfirmRemoveDialog';

interface RemoveCardDialogProps {
  /** The card pending removal; `null` closes the dialog. Controlled like `EditCreditCardModal`. */
  card: CreditCardAccount | null;
  onClose: () => void;
  /** Called after a successful removal — the caller decides toast/close, same contract as Edit. */
  onSuccess?: () => void;
}

/** Confirms credit card removal. Supplies card-specific copy/preview/mutation to `ConfirmRemoveDialog`. */
export function RemoveCardDialog({ card, onClose, onSuccess }: RemoveCardDialogProps) {
  const { t } = useTranslation('members');
  const mutation = useRemoveCreditCard({ onSuccess });

  return (
    <ConfirmRemoveDialog
      open={card !== null}
      onOpenChange={(open) => {
        if (!open && !mutation.isPending) onClose();
      }}
      isPending={mutation.isPending}
      title={t('payments.pages.paymentAccounts.creditCards.removeConfirmTitle')}
      description={t('payments.pages.paymentAccounts.creditCards.removeConfirmDescription')}
      preview={
        card && (
          <>
            <span className="text-muted-foreground">
              <Icon name="creditCard" />
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold text-foreground">
                {`${card.network} ···· ···· ···· ${card.last4}`}
              </span>
            </span>
          </>
        )
      }
      cancelLabel={t('payments.pages.paymentAccounts.creditCards.removeConfirmCancel')}
      confirmLabel={t('payments.pages.paymentAccounts.creditCards.removeConfirmAction')}
      confirmSubmittingLabel={t('payments.pages.paymentAccounts.creditCards.removeConfirmSubmitting')}
      onCancel={onClose}
      onConfirm={() => card && mutation.submit(card.id)}
    />
  );
}
